import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages custom design products in the cart.
 * Uses _logo_link_id property as primary linking method, with parent_relationship as fallback.
 *
 * How it works:
 * 1. Monitors cart updates (add, change, remove)
 * 2. Checks for custom design products linked via _logo_link_id property OR parent_relationship
 * 3. If a main product is removed, removes the linked custom design product
 *
 * @extends {Component}
 */
class CartCustomImageHandlerComponent extends Component {
  /** @type {string | null} The variant ID of the custom design product */
  #customDesignVariantId = null;

  /** @type {boolean} Whether the handler is currently processing a change */
  #isProcessing = false;

  connectedCallback() {
    super.connectedCallback();

    // Get the custom design product variant ID from the component attribute
    this.#customDesignVariantId = this.dataset.customDesignVariantId;

    if (!this.#customDesignVariantId) {
      console.warn('CartCustomImageHandler: No custom design variant ID provided');
      return;
    }

    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Run initial check on page load
    this.#checkAndUpdateCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles cart update events
   * @param {CartUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    // Avoid recursive updates from our own changes
    if (event.detail.data?.source === 'custom-image-handler') {
      return;
    }

    this.#checkAndUpdateCart();
  };

  /**
   * Main logic to check cart and clean up orphaned custom design products
   */
  async #checkAndUpdateCart() {
    // Prevent concurrent processing
    if (this.#isProcessing) {
      return;
    }

    this.#isProcessing = true;

    try {
      // Fetch current cart state
      const cart = await this.#fetchCart();

      if (!cart || !cart.items) {
        return;
      }

      // Build maps for lookups
      const existingLineKeys = new Set(cart.items.map(item => item.key));
      const mainProductLinkIds = this.#getMainProductLinkIds(cart.items);

      // Find custom design products and check if they're orphaned
      const orphanedCustomDesignProducts = this.#findOrphanedCustomDesignProducts(cart.items, existingLineKeys, mainProductLinkIds);

      // Remove orphaned custom design products
      if (orphanedCustomDesignProducts.length > 0) {
        await this.#removeOrphanedProducts(orphanedCustomDesignProducts);
      }
    } catch (error) {
      console.error('CartCustomImageHandler: Error checking cart', error);
    } finally {
      this.#isProcessing = false;
    }
  }

  /**
   * Fetches the current cart state
   * @returns {Promise<Object>} The cart object
   */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}?view=json`, fetchConfig('json'));
      return await response.json();
    } catch (error) {
      console.error('CartCustomImageHandler: Error fetching cart', error);
      return null;
    }
  }

  /**
   * Gets all _logo_link_id values from main products (products with my-design engraving option)
   * @param {Array} items - Cart items
   * @returns {Set} Set of link IDs from main products
   */
  #getMainProductLinkIds(items) {
    const linkIds = new Set();

    items.forEach((item) => {
      // Skip custom design products themselves
      if (item.variant_id === parseInt(this.#customDesignVariantId, 10)) {
        return;
      }

      if (!item.properties) return;

      // Check for _logo_link_id property on main products
      const linkIdProp = Object.entries(item.properties).find(
        ([key]) => key.toLowerCase() === '_logo_link_id'
      );

      if (linkIdProp && linkIdProp[1]) {
        linkIds.add(linkIdProp[1]);
      }
    });

    return linkIds;
  }

  /**
   * Finds custom design products whose parent no longer exists in the cart
   * Uses _logo_link_id as primary, parent_relationship as fallback
   * @param {Array} items - Cart items
   * @param {Set} existingLineKeys - Set of all current cart line keys
   * @param {Set} mainProductLinkIds - Set of _logo_link_id values from main products
   * @returns {Array} Orphaned custom design product line items
   */
  #findOrphanedCustomDesignProducts(items, existingLineKeys, mainProductLinkIds) {
    const orphaned = [];

    items.forEach((item) => {
      // Check if this is our custom design product
      if (item.variant_id !== parseInt(this.#customDesignVariantId, 10)) {
        return;
      }

      // PRIMARY: Check for _logo_link_id property-based linking
      if (item.properties) {
        const linkIdProp = Object.entries(item.properties).find(
          ([key]) => key.toLowerCase() === '_logo_link_id'
        );

        if (linkIdProp && linkIdProp[1]) {
          // Check if any main product has this link ID
          if (!mainProductLinkIds.has(linkIdProp[1])) {
            // No main product with this link ID - orphaned
            orphaned.push(item);
          }
          return; // Property-based linking takes precedence
        }
      }

      // FALLBACK: Check parent_relationship (from parent_id system)
      if (item.parent_relationship && item.parent_relationship.parent_key) {
        // Check if the parent still exists
        if (!existingLineKeys.has(item.parent_relationship.parent_key)) {
          // Parent was removed - this custom design product is orphaned
          orphaned.push(item);
        }
      }
    });

    return orphaned;
  }

  /**
   * Removes orphaned custom design products from the cart
   * @param {Array} orphanedProducts - Orphaned line items to remove
   */
  async #removeOrphanedProducts(orphanedProducts) {
    if (orphanedProducts.length === 0) return;

    try {
      const updates = {};
      orphanedProducts.forEach((line) => {
        updates[line.key] = 0;
      });

      const response = await fetch(`${Theme.routes.cart_update_url}`, {
        ...fetchConfig('json', { body: JSON.stringify({ updates }) }),
      });

      if (!response.ok) {
        console.error('CartCustomImageHandler: Error removing orphaned custom design products');
        return;
      }

      const result = await response.json();

      // Dispatch cart update event
      document.dispatchEvent(
        new CartUpdateEvent(
          result,
          'cart-custom-image-handler',
          {
            source: 'custom-image-handler',
            itemCount: result.item_count,
          }
        )
      );

      console.log('CartCustomImageHandler: Removed orphaned custom design products');
    } catch (error) {
      console.error('CartCustomImageHandler: Error removing orphaned custom design products', error);
    }
  }
}

if (!customElements.get('cart-custom-image-handler')) {
  customElements.define('cart-custom-image-handler', CartCustomImageHandlerComponent);
}
